import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import { useToast } from '@tradener/lumen';

import CREDIT_COUNTERPART_LIST, {
  CreditCounterpartyProps,
  PayloadProps as CounterpartiesPayloadProps,
  ResponseProps as CounterpartiesResponseProps,
} from '@graphql/query/operationLimits/credit/counterpartList';

import useOperationLimitsCompetenceFilters from '../../hooks/useOperationLimitsCompetenceFilters';
import {
  CreditSortDirectionProps,
  CreditSortFieldProps,
  OperationLimitsDashboardActionProps,
  OperationLimitsDashboardStateProps,
  UseOperationLimitsDashboardReturnProps,
} from '../types/OperationLimitsDashboard';
import { fromCounterpartyRatingFilterValue, toCounterpartyRatingFilterValue } from '../utils/rating';

export const initialState: OperationLimitsDashboardStateProps = {
  search: '',
  hasExposure: true,
  sortField: 'EXPOSURE',
  sortDirection: 'DESC',
};

export const operationLimitsDashboardReducer = (
  state: OperationLimitsDashboardStateProps,
  action: OperationLimitsDashboardActionProps,
): OperationLimitsDashboardStateProps => {
  if (action.type === 'SET_SEARCH') {
    return { ...state, search: action.payload };
  }

  if (action.type === 'SET_HAS_EXPOSURE') {
    return { ...state, hasExposure: action.payload };
  }

  if (action.type === 'SET_SORT') {
    if (state.sortField === action.payload) {
      return {
        ...state,
        sortDirection: state.sortDirection === 'DESC' ? 'ASC' : 'DESC',
      };
    }

    return {
      ...state,
      sortField: action.payload,
      sortDirection: 'DESC',
    };
  }

  return state;
};

const sortCounterparties = (
  items: CreditCounterpartyProps[],
  field: CreditSortFieldProps,
  direction: CreditSortDirectionProps,
): CreditCounterpartyProps[] => {
  const valueByField: Record<Exclude<CreditSortFieldProps, 'COUNTERPARTY'>, keyof CreditCounterpartyProps> = {
    EXPOSURE: 'exposureMtmFiltered',
    EXPOSURE_GLOBAL: 'exposureMtmGlobal',
    BALANCE: 'financialBalance',
  };
  const sign = direction === 'ASC' ? 1 : -1;

  return [...items].sort((left, right) => {
    if (field === 'COUNTERPARTY') {
      return left.counterpartName.localeCompare(right.counterpartName) * sign;
    }

    const valueField = valueByField[field];
    const leftValue = Number(left[valueField]);
    const rightValue = Number(right[valueField]);

    if (leftValue === rightValue) {
      return left.counterpartName.localeCompare(right.counterpartName);
    }

    return (leftValue - rightValue) * sign;
  });
};

function useOperationLimitsDashboard(): UseOperationLimitsDashboardReturnProps {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(operationLimitsDashboardReducer, initialState);
  const { queryFilters } = useOperationLimitsCompetenceFilters();
  const selectedRating = fromCounterpartyRatingFilterValue(searchParams.get('rating'));
  const rating = selectedRating ? toCounterpartyRatingFilterValue(selectedRating) : undefined;

  const variables = useMemo<CounterpartiesPayloadProps>(
    () => ({
      filter: {
        counterpartName: state.search || undefined,
        hasExposure: state.hasExposure,
        rating,
        startCompetence: queryFilters.startCompetence,
        endCompetence: queryFilters.endCompetence,
      },
    }),
    [rating, queryFilters.endCompetence, queryFilters.startCompetence, state.hasExposure, state.search],
  );

  const { data, loading, error } = useQuery<CounterpartiesResponseProps, CounterpartiesPayloadProps>(CREDIT_COUNTERPART_LIST, {
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const onSearch = useCallback((value: string) => {
    dispatch({ type: 'SET_SEARCH', payload: value });
  }, []);

  const onSort = useCallback((field: CreditSortFieldProps) => {
    dispatch({ type: 'SET_SORT', payload: field });
  }, []);

  const onToggleHasExposure = useCallback((value: boolean) => {
    dispatch({ type: 'SET_HAS_EXPOSURE', payload: value });
  }, []);

  const onLoadMore = useCallback(() => Promise.resolve(), []);

  useEffect(() => {
    if (!error) {
      return;
    }

    toast({ title: 'Erro', status: 'error', description: error.message || 'Erro ao carregar exposição por contraparte.' });
  }, [error, toast]);

  const rawCounterparties = useMemo(() => data?.creditCounterpartList || [], [data?.creditCounterpartList]);
  const counterparties = useMemo(
    () => sortCounterparties(rawCounterparties, state.sortField, state.sortDirection),
    [rawCounterparties, state.sortField, state.sortDirection],
  );

  return {
    search: state.search,
    hasExposure: state.hasExposure,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    loading,
    loadingMore: false,
    counterparties,
    hasMore: false,
    onSearch,
    onToggleHasExposure,
    onSort,
    onLoadMore,
  };
}

export default useOperationLimitsDashboard;
